// Admin Products — structured fields, WYSIWYG editor, dynamic key-value specs
import React, { useState, useEffect, useCallback } from "react";
import {
  Plus, Edit2, Trash2, Search, X, Loader, RefreshCw,
  Save, Package, ChevronDown, ChevronUp, CheckCircle,
  AlertCircle, Image as ImageIcon, Eye, EyeOff
} from "lucide-react";
import { productsAPI, brandsAPI, uploadAPI } from "../../services/api";
import { getImageUrl } from "../../utils/image";
import { WysiwygEditor } from "../../components/ui/index";

const toSlug = s => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// ── Additional Images Manager ────────────────────────────
function ProductImagesEditor({ value, onChange }) {
  const [images, setImages] = useState(() => {
    return (value || "").split(",").map(s => s.trim()).filter(Boolean);
  });

  useEffect(() => {
    setImages((value || "").split(",").map(s => s.trim()).filter(Boolean));
  }, [value]);

  const sync = (updated) => {
    setImages(updated);
    onChange(updated.join(","));
  };

  const addImage = (url) => {
    if (url && !images.includes(url)) {
      sync([...images, url]);
    }
  };

  const removeImage = (i) => {
    sync(images.filter((_, idx) => idx !== i));
  };

  const [inputUrl, setInputUrl] = useState("");

  return (
    <div className="space-y-3">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {images.map((img, i) => (
            <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border border-gray-100 group">
              <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeImage(i)}
                className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          className="input-field text-xs flex-1"
          placeholder="Paste external image URL..."
          value={inputUrl}
          onChange={e => setInputUrl(e.target.value)}
        />
        <button
          type="button"
          onClick={() => { if (inputUrl.trim()) { addImage(inputUrl.trim()); setInputUrl(""); } }}
          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-semibold"
        >
          Add URL
        </button>
        <ImgUploadBtn onUploaded={url => addImage(url)} type="products" label="Upload Image" />
      </div>
    </div>
  );
}


const emptyProduct = {
  name: "", slug: "", category: "", brand: "", shortDesc: "", description: "",
  origin: "", packaging: "", availability: "", minOrder: "",
  price: "", imageUrl: "", images: "",
  specifications: "", features: "",
  featured: false, isActive: true, sortOrder: 0,
  metaTitle: "", metaDesc: "", focusKeyword: "",
};

// ── Dynamic Key-Value Spec Editor ───────────────────────
function SpecEditor({ value, onChange }) {
  const [pairs, setPairs] = useState(() => {
    try {
      const parsed = JSON.parse(value || "{}");
      return Object.entries(parsed).map(([k, v]) => ({ key: k, val: String(v) }));
    } catch { return []; }
  });

  const sync = (updated) => {
    setPairs(updated);
    const obj = {};
    updated.forEach(p => { if (p.key.trim()) obj[p.key.trim()] = p.val; });
    onChange(JSON.stringify(obj));
  };

  const addRow = () => sync([...pairs, { key: "", val: "" }]);
  const removeRow = (i) => sync(pairs.filter((_, idx) => idx !== i));
  const update = (i, field, v) => sync(pairs.map((p, idx) => idx === i ? { ...p, [field]: v } : p));

  return (
    <div className="space-y-2">
      {pairs.map((pair, i) => (
        <div key={i} className="flex gap-2 items-center">
          <input
            className="input-field text-xs py-2 flex-1"
            placeholder="Specification name (e.g. Origin)"
            value={pair.key}
            onChange={e => update(i, "key", e.target.value)}
          />
          <span className="text-gray-400 text-sm flex-shrink-0">:</span>
          <input
            className="input-field text-xs py-2 flex-1"
            placeholder="Value (e.g. India)"
            value={pair.val}
            onChange={e => update(i, "val", e.target.value)}
          />
          <button
            type="button"
            onClick={() => removeRow(i)}
            className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-400 hover:bg-red-100 flex-shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1.5 text-xs text-brand-primary font-semibold hover:underline mt-1"
      >
        <Plus className="w-3.5 h-3.5" /> Add Specification
      </button>
    </div>
  );
}

// ── Dynamic Features List Editor ────────────────────────
function FeaturesEditor({ value, onChange }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(value || "[]"); } catch { return []; }
  });

  const sync = (updated) => { setItems(updated); onChange(JSON.stringify(updated)); };
  const add = () => sync([...items, ""]);
  const remove = (i) => sync(items.filter((_, idx) => idx !== i));
  const update = (i, v) => sync(items.map((item, idx) => idx === i ? v : item));

  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex gap-2 items-center">
          <input
            className="input-field text-xs py-2 flex-1"
            placeholder="e.g. ISO certified, Export quality..."
            value={item}
            onChange={e => update(i, e.target.value)}
          />
          <button type="button" onClick={() => remove(i)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-400 hover:bg-red-100 flex-shrink-0">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button type="button" onClick={add} className="flex items-center gap-1.5 text-xs text-brand-primary font-semibold hover:underline mt-1">
        <Plus className="w-3.5 h-3.5" /> Add Feature
      </button>
    </div>
  );
}

// ── Image Upload Button ──────────────────────────────────
function ImgUploadBtn({ onUploaded, type = "products", label = "Upload" }) {
  const [uploading, setUploading] = useState(false);
  return (
    <label className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold cursor-pointer transition-colors flex-shrink-0 ${uploading ? "opacity-50" : "hover:bg-brand-pale hover:border-brand-primary text-gray-600"}`}>
      {uploading ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
      {label}
      <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={async e => {
        const f = e.target.files?.[0]; if (!f) return;
        setUploading(true);
        try { const { data } = await uploadAPI.single(f, type); onUploaded(data.url); }
        catch { alert("Upload failed"); }
        finally { setUploading(false); }
      }} />
    </label>
  );
}

// ── Section Toggle ────────────────────────────────────────
function Section({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-100 rounded-2xl overflow-hidden">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-gray-50 hover:bg-gray-100 transition-colors">
        <span className="font-semibold text-gray-700 text-sm">{title}</span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="p-5 space-y-4">{children}</div>}
    </div>
  );
}

// ── Product Form Modal ────────────────────────────────────
function ProductForm({ item, onSave, onClose }) {
  const [form, setForm] = useState({
    ...emptyProduct,
    ...(item || {}),
    slug: item?.slug || toSlug(item?.name || "")
  });
  const [isCustomSlug, setIsCustomSlug] = useState(Boolean(item?.slug));
  const [brandsList, setBrandsList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    brandsAPI.getAll().then(r => setBrandsList(r.data.brands || [])).catch(() => {});
  }, []);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));
  const setE = k => e => set(k)(e.target.value);
  const setC = k => e => set(k)(e.target.checked);

  const handleNameChange = (e) => {
    const val = e.target.value;
    setForm(f => ({
      ...f,
      name: val,
      slug: !isCustomSlug ? toSlug(val) : f.slug
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError("Product name is required"); return; }
    setSaving(true); setError("");
    try {
      await onSave({
        ...form,
        slug: form.slug ? toSlug(form.slug) : toSlug(form.name)
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || "Save failed. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm px-4 py-6 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full my-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-3xl z-10">
          <h3 className="font-heading font-bold text-gray-900 text-lg">
            {item ? "Edit Product" : "Add New Product"}
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200">
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="mx-5 mt-4 flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
          </div>
        )}

        <div className="p-5 space-y-4">
          {/* ── Basic Info ── */}
          <Section title="Basic Information">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Product Name *</label>
                <input className="input-field" placeholder="e.g. Premium Basmati Rice" value={form.name} onChange={handleNameChange} />
              </div>

              {/* Custom URL Slug */}
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-gray-600">Custom URL Slug *</label>
                  <button
                    type="button"
                    onClick={() => {
                      const auto = toSlug(form.name);
                      set("slug")(auto);
                      setIsCustomSlug(false);
                    }}
                    className="text-[11px] text-brand-primary hover:underline font-semibold"
                  >
                    Generate from Name
                  </button>
                </div>
                <div className="flex items-center">
                  <span className="text-xs text-gray-500 bg-gray-100 px-3 py-2 border border-r-0 border-gray-200 rounded-l-xl select-none font-mono">
                    /products/
                  </span>
                  <input
                    className="input-field rounded-l-none font-mono text-xs flex-1"
                    placeholder="e.g. premium-basmati-rice"
                    value={form.slug || ""}
                    onChange={e => {
                      setIsCustomSlug(true);
                      set("slug")(toSlug(e.target.value));
                    }}
                  />
                </div>
                <p className="text-[11px] text-gray-400 mt-1">
                  Target URL: <span className="font-mono text-gray-700">savafresh.com/products/{form.slug || toSlug(form.name)}</span> (Automatic 301 redirects are preserved if changed)
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Category</label>
                <input className="input-field" placeholder="e.g. Grains & Rice" value={form.category} onChange={setE("category")} />
              </div>

              {/* Brand Selector */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Brand</label>
                <div className="flex gap-2">
                  <select
                    className="input-field text-sm flex-1"
                    value={brandsList.some(b => b.name === form.brand) ? form.brand : (form.brand ? "__custom__" : "")}
                    onChange={e => {
                      if (e.target.value !== "__custom__") {
                        set("brand")(e.target.value);
                      }
                    }}
                  >
                    <option value="">-- Select Brand / Default --</option>
                    {brandsList.map(b => (
                      <option key={b.id} value={b.name}>{b.name}</option>
                    ))}
                    {form.brand && !brandsList.some(b => b.name === form.brand) && (
                      <option value="__custom__">Custom: {form.brand}</option>
                    )}
                  </select>
                  <input
                    className="input-field text-sm flex-1"
                    placeholder="Or custom brand..."
                    value={form.brand || ""}
                    onChange={setE("brand")}
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Price / Pricing Note (Optional)</label>
                <input className="input-field" placeholder="e.g. $1.50/kg or Contact for wholesale pricing" value={form.price} onChange={setE("price")} />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Short Description (shown on listing cards)</label>
                <textarea className="input-field resize-none" rows={2} placeholder="Brief 1–2 sentence description for product listing..." value={form.shortDesc} onChange={setE("shortDesc")} />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Description (shown on detail page)</label>
                <WysiwygEditor value={form.description} onChange={val => set("description")(val)} />
              </div>
            </div>
          </Section>

          {/* ── Product Details ── */}
          <Section title="Product Details">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Origin / Source Country</label>
                <input className="input-field" placeholder="e.g. India, UAE, Brazil" value={form.origin} onChange={setE("origin")} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Packaging Options</label>
                <input className="input-field" placeholder="e.g. 5kg, 10kg, 25kg, 50kg bags" value={form.packaging} onChange={setE("packaging")} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Availability</label>
                <input className="input-field" placeholder="e.g. Year-round, Seasonal" value={form.availability} onChange={setE("availability")} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Minimum Order Quantity</label>
                <input className="input-field" placeholder="e.g. 5 MT, 100 cartons" value={form.minOrder} onChange={setE("minOrder")} />
              </div>
            </div>
          </Section>

          {/* ── Specifications ── */}
          <Section title="Specifications (Key-Value Pairs)">
            <p className="text-xs text-gray-400 -mt-2 mb-3">Add structured specifications like grain length, moisture content, purity, etc.</p>
            <SpecEditor value={form.specifications} onChange={val => set("specifications")(val)} />
          </Section>

          {/* ── Features ── */}
          <Section title="Key Features / Highlights">
            <p className="text-xs text-gray-400 -mt-2 mb-3">Add bullet-point features that appear on the product detail page.</p>
            <FeaturesEditor value={form.features} onChange={val => set("features")(val)} />
          </Section>

          {/* ── Images ── */}
          <Section title="Images">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Main Product Image</label>
              <div className="flex gap-2">
                <input className="input-field text-sm flex-1" placeholder="Image URL or upload..." value={form.imageUrl} onChange={setE("imageUrl")} />
                <ImgUploadBtn onUploaded={url => set("imageUrl")(url)} type="products" />
              </div>
              {form.imageUrl && (
                <img src={getImageUrl(form.imageUrl)} alt="Preview" className="mt-2 h-28 w-auto rounded-xl object-cover border border-gray-100" onError={e => e.target.style.display = "none"} />
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Product Gallery / Additional Images</label>
              <ProductImagesEditor value={form.images} onChange={val => set("images")(val)} />
            </div>
          </Section>

          {/* ── SEO ── */}
          <Section title="SEO Settings" defaultOpen={true}>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-gray-600">Meta Title</label>
                <span className={`text-xs ${(form.metaTitle || "").length > 60 ? "text-red-500" : "text-gray-400"}`}>{(form.metaTitle || "").length}/60</span>
              </div>
              <input className="input-field text-sm" placeholder="e.g. Premium Basmati Rice Sourcing | Sava Fresh" value={form.metaTitle} onChange={setE("metaTitle")} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-gray-600">Meta Description</label>
                <span className={`text-xs ${(form.metaDesc || "").length > 160 ? "text-red-500" : "text-gray-400"}`}>{(form.metaDesc || "").length}/160</span>
              </div>
              <textarea className="input-field resize-none text-sm" rows={2} placeholder="Brief summary for Google search results..." value={form.metaDesc} onChange={setE("metaDesc")} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-bold text-brand-primary uppercase tracking-wide">Focus Keyword</label>
                <span className="text-[11px] text-gray-500 font-medium">Primary target search query</span>
              </div>
              <input className="input-field text-sm font-medium" placeholder="e.g. basmati rice uae export" value={form.focusKeyword || ""} onChange={setE("focusKeyword")} />
              <p className="text-[11px] text-gray-500 mt-1">
                Enter the primary search keyword this product should rank for on Google and search engines.
              </p>
            </div>

            {/* Real-time SEO Analysis Checklist */}
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 space-y-2">
              <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">Live SEO Optimization Checklist</h4>
              {(() => {
                const kw = (form.focusKeyword || "").trim().toLowerCase();
                const nameHas = kw && (form.name || "").toLowerCase().includes(kw);
                const slugHas = kw && (form.slug || toSlug(form.name)).includes(toSlug(kw));
                const descHas = kw && (form.metaDesc || "").toLowerCase().includes(kw);
                const contentHas = kw && (form.description || "").toLowerCase().includes(kw);
                return (
                  <>
                    <div className="flex items-center justify-between text-xs py-1 border-b border-gray-100">
                      <span className="text-gray-600">Focus Keyword in Product Name</span>
                      {kw ? (nameHas ? <span className="font-semibold text-green-600">✓ Present</span> : <span className="font-semibold text-red-500">✗ Missing</span>) : <span className="text-gray-400">— Enter keyword</span>}
                    </div>
                    <div className="flex items-center justify-between text-xs py-1 border-b border-gray-100">
                      <span className="text-gray-600">Focus Keyword in Custom URL Slug</span>
                      {kw ? (slugHas ? <span className="font-semibold text-green-600">✓ Present</span> : <span className="font-semibold text-amber-500">⚠ Not in slug</span>) : <span className="text-gray-400">— Enter keyword</span>}
                    </div>
                    <div className="flex items-center justify-between text-xs py-1 border-b border-gray-100">
                      <span className="text-gray-600">Focus Keyword in Meta Description</span>
                      {kw ? (descHas ? <span className="font-semibold text-green-600">✓ Present</span> : <span className="font-semibold text-amber-500">⚠ Recommended</span>) : <span className="text-gray-400">— Enter keyword</span>}
                    </div>
                    <div className="flex items-center justify-between text-xs py-1">
                      <span className="text-gray-600">Focus Keyword in Product Description</span>
                      {kw ? (contentHas ? <span className="font-semibold text-green-600">✓ Present</span> : <span className="font-semibold text-amber-500">⚠ Recommended</span>) : <span className="text-gray-400">— Enter keyword</span>}
                    </div>
                  </>
                );
              })()}
            </div>
          </Section>

          {/* ── Options ── */}
          <div className="flex flex-wrap gap-6 px-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={setC("featured")} className="w-4 h-4 accent-brand-primary" />
              <span className="text-sm font-medium text-gray-700">Mark as Featured</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.isActive} onChange={setC("isActive")} className="w-4 h-4 accent-brand-primary" />
              <span className="text-sm font-medium text-gray-700">Active (visible on site)</span>
            </label>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Sort Order:</label>
              <input type="number" className="input-field w-20 py-1.5 text-sm" value={form.sortOrder} onChange={setE("sortOrder")} min="0" />
            </div>
          </div>
        </div>

        <div className="flex gap-3 p-5 border-t border-gray-100 bg-gray-50 rounded-b-3xl">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-white transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 btn-primary justify-center py-2.5 text-sm">
            {saving ? <><Loader className="w-4 h-4 animate-spin" />Saving...</> : <><Save className="w-4 h-4" />{item ? "Update" : "Add"} Product</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Admin Products Page ─────────────────────────────
export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "active", "inactive"
  const [saved, setSaved] = useState(false);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      // Request all products (both active and inactive) so admin can see and manage everything
      const r = await productsAPI.getAll({ status: "all" });
      setItems(r.data.products || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleSave = async (form) => {
    const payload = { 
      ...form, 
      description: form.description ? "base64:" + btoa(unescape(encodeURIComponent(form.description))) : "" 
    };
    if (editItem) await productsAPI.update(editItem.id, payload);
    else await productsAPI.create(payload);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
    fetchProducts();
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await productsAPI.toggleStatus(id);
      setItems(prev => prev.map(p => p.id === id ? { ...p, isActive: !currentStatus } : p));
    } catch (err) {
      alert("Failed to update status. Please try again.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product permanently?")) return;
    try { await productsAPI.delete(id); fetchProducts(); } catch {}
  };

  const totalCount = items.length;
  const activeCount = items.filter(i => i.isActive).length;
  const inactiveCount = items.filter(i => !i.isActive).length;

  const filtered = items.filter(i => {
    // Status tab filter
    if (statusFilter === "active" && !i.isActive) return false;
    if (statusFilter === "inactive" && i.isActive) return false;

    // Search filter
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      i.name.toLowerCase().includes(q) || 
      (i.category || "").toLowerCase().includes(q) ||
      (i.brand || "").toLowerCase().includes(q) ||
      (i.focusKeyword || "").toLowerCase().includes(q) ||
      (i.slug || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-gray-900 text-xl">Products</h2>
          <p className="text-gray-400 text-sm">
            {totalCount} total products · {activeCount} active · {inactiveCount} inactive / hidden
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {saved && <span className="flex items-center gap-1.5 text-green-700 text-sm bg-green-50 px-3 py-1.5 rounded-xl border border-green-200"><CheckCircle className="w-4 h-4" />Saved!</span>}
          <button onClick={fetchProducts} className="w-9 h-9 flex items-center justify-center bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-soft"><RefreshCw className="w-4 h-4 text-gray-500" /></button>
          <button onClick={() => { setEditItem(null); setShowForm(true); }} className="btn-primary text-xs px-4 py-2.5"><Plus className="w-4 h-4" /> Add Product</button>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        {/* Status Filter Tabs */}
        <div className="flex items-center bg-gray-100 p-1 rounded-xl border border-gray-200 text-xs font-semibold">
          <button
            type="button"
            onClick={() => setStatusFilter("all")}
            className={`px-3 py-1.5 rounded-lg transition-all ${
              statusFilter === "all"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            All ({totalCount})
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter("active")}
            className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
              statusFilter === "active"
                ? "bg-white text-green-700 shadow-sm"
                : "text-gray-500 hover:text-green-700"
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
            Active ({activeCount})
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter("inactive")}
            className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
              statusFilter === "inactive"
                ? "bg-white text-amber-700 shadow-sm"
                : "text-gray-500 hover:text-amber-700"
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
            Inactive / Hidden ({inactiveCount})
          </button>
        </div>

        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input className="input-field pl-9 py-2 text-sm w-full" placeholder="Search products, brands, keywords..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader className="w-6 h-6 text-brand-primary animate-spin mr-2" /><span className="text-gray-400 text-sm">Loading products...</span></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(product => {
            let specCount = 0;
            try { specCount = Object.keys(JSON.parse(product.specifications || "{}")).length; } catch {}
            let featCount = 0;
            try { featCount = JSON.parse(product.features || "[]").length; } catch {}
            return (
              <div 
                key={product.id} 
                className={`bg-white rounded-2xl shadow-card hover:shadow-hover transition-all duration-300 overflow-hidden flex flex-col border ${
                  !product.isActive ? "border-amber-200 bg-amber-50/20" : "border-transparent"
                }`}
              >
                <div className="h-36 bg-brand-pale overflow-hidden relative">
                  {product.imageUrl
                    ? <img src={getImageUrl(product.imageUrl)} alt={product.name} className={`w-full h-full object-cover ${!product.isActive ? "opacity-60 grayscale-[40%]" : ""}`} onError={e => e.target.style.display = "none"} />
                    : <div className="w-full h-full flex items-center justify-center"><Package className="w-10 h-10 text-brand-muted" /></div>}
                  
                  {product.featured && <div className="absolute top-2 right-2 badge-gold text-xs shadow-sm">★ Featured</div>}
                  
                  {/* Status Badge */}
                  <div className="absolute top-2 left-2 flex items-center gap-1.5">
                    {product.isActive ? (
                      <span className="bg-green-600/90 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full shadow-sm">
                        ✓ Active
                      </span>
                    ) : (
                      <span className="bg-amber-600/90 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full shadow-sm">
                        ✕ Inactive (Hidden)
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <h3 className="font-heading font-bold text-gray-900 text-sm leading-tight">{product.name}</h3>
                  </div>

                  <div className="flex items-center gap-1.5 flex-wrap mb-2">
                    {product.brand && <span className="badge-gold text-[10px] font-bold uppercase tracking-wider">{product.brand}</span>}
                    {product.category && <span className="badge-brand text-[10px]">{product.category}</span>}
                  </div>

                  {product.slug && (
                    <p className="text-[11px] text-gray-400 font-mono truncate mb-2">
                      <span className="text-gray-300">/products/</span><span className="text-brand-primary font-medium">{product.slug}</span>
                    </p>
                  )}

                  {product.focusKeyword && (
                    <div className="mb-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-semibold border border-emerald-100">
                        🎯 {product.focusKeyword}
                      </span>
                    </div>
                  )}

                  {product.shortDesc && <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 mb-2">{product.shortDesc}</p>}
                  
                  <div className="flex gap-3 text-xs text-gray-400 mb-4 mt-auto">
                    {specCount > 0 && <span>{specCount} spec{specCount !== 1 ? "s" : ""}</span>}
                    {featCount > 0 && <span>{featCount} feature{featCount !== 1 ? "s" : ""}</span>}
                    {product.price && <span className="text-brand-primary font-semibold ml-auto">{product.price}</span>}
                  </div>

                  <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                    <button 
                      onClick={() => handleToggleStatus(product.id, product.isActive)}
                      title={product.isActive ? "Hide from website" : "Make visible on website"}
                      className={`flex items-center justify-center gap-1 px-2.5 py-2 rounded-xl text-xs font-semibold transition-colors ${
                        product.isActive 
                          ? "bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-700" 
                          : "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
                      }`}
                    >
                      {product.isActive ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      {product.isActive ? "Hide" : "Publish"}
                    </button>

                    <button onClick={() => { setEditItem(product); setShowForm(true); }} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-brand-pale text-brand-primary text-xs font-semibold hover:bg-brand-muted transition-colors">
                      <Edit2 className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button onClick={() => handleDelete(product.id)} className="flex items-center justify-center px-3 py-2 rounded-xl bg-red-50 text-red-500 text-xs font-semibold hover:bg-red-100 transition-colors" title="Delete product">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-3 text-center py-16">
              <Package className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 font-medium">No products found for this filter</p>
              {statusFilter !== "all" && (
                <button onClick={() => setStatusFilter("all")} className="mt-2 text-xs text-brand-primary hover:underline font-semibold">
                  Show all products
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {showForm && (
        <ProductForm
          item={editItem}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditItem(null); }}
        />
      )}
    </div>
  );
}
